import { usePhoneViewport } from "@/hooks/use-phone-viewport";
import { FaroButton, FaroText } from "@faro-lotv/flat-ui";
import { Box, Stack, useMediaQuery } from "@mui/material";
import {
  useEffect,
  useRef,
  useState,
  type JSX,
  type PointerEvent,
} from "react";
import { useAnnotationShapesStore } from "./annotation-shapes-store";
import { ColorDot } from "./color-dot";

/** Height, in pixels, of the bar the sheet keeps on screen when minimized, without the safe area */
export const ANNOTATION_SHEET_BAR_HEIGHT = 56;

type Motion = { ms: number; easing: string; delayMs?: number };

/**
 * How the sheet moves. It covers the whole screen, so it enters calmly: after a short pause to see the finished shape,
 * with a slow settle at the end. It leaves faster, accelerating away, and moves between full and minimized in between.
 */
const MOTION: Record<"enter" | "exit" | "toggle", Motion> = {
  enter: { ms: 500, easing: "cubic-bezier(0.2, 0, 0, 1)", delayMs: 150 },
  exit: { ms: 300, easing: "cubic-bezier(0.3, 0, 1, 1)" },
  toggle: { ms: 400, easing: "cubic-bezier(0.2, 0, 0, 1)" },
};

/** The form fades in behind the sheet, so it does not arrive at once with it */
const CONTENT_FADE_MS = 300;

/** A vertical swipe on the bar longer than this, in pixels, minimizes or expands the sheet */
const SWIPE_PX = 40;

/** @returns true while the phone creation sheet is minimized to its bar at the bottom of the canvas */
export function useIsAnnotationSheetMinimized(): boolean {
  const isPhone = usePhoneViewport();
  const isMinimized = useAnnotationShapesStore(
    (s) => !!s.panelContent && s.isSheetMinimized,
  );
  return isPhone && isMinimized;
}

/** @param isSheetMinimized true to minimize the sheet to its bar, false to show it full screen */
function setMinimized(isSheetMinimized: boolean): void {
  useAnnotationShapesStore.setState({ isSheetMinimized });
}

type AnnotationCreationSheetProps = {
  /** Content of the sheet, undefined to close it */
  content?: JSX.Element;
};

/**
 * @returns the sheet where annotations are created on phones. It opens full screen, since the details are what the user
 * works on and the keyboard takes half of the screen anyway. It has only two positions, full or minimized to a bar,
 * to look at the shape on a clean canvas and redraw it, without losing what was filled in.
 */
export function AnnotationCreationSheet({
  content,
}: AnnotationCreationSheetProps): JSX.Element | null {
  const isMinimized = useAnnotationShapesStore((s) => s.isSheetMinimized);
  const title = useAnnotationShapesStore((s) => s.panelTitle);
  const hasStyle = useAnnotationShapesStore((s) => !!s.panelStyleKind);
  // The color shown on the Style button: of the annotation being edited, or of the one just placed
  const color = useAnnotationShapesStore((s) => {
    if (s.editedShape) return s.editedShape.color;
    return s.panelStyleKind === "shape" ? s.style.color : s.pointColor;
  });

  // Keep the last content while sliding out, so it does not disappear before the sheet is closed,
  // and slide in from the bottom on the frame after mounting
  const [shown, setShown] = useState(content);
  const [phase, setPhase] = useState<"out" | "entering" | "in">("out");
  const isOpen = !!content;
  useEffect(() => {
    if (content) setShown(content);
  }, [content]);
  useEffect(() => {
    if (isOpen) {
      const frame = requestAnimationFrame(() => setPhase("entering"));
      const { ms, delayMs = 0 } = MOTION.enter;
      const timeout = setTimeout(() => setPhase("in"), ms + delayMs);
      return () => {
        cancelAnimationFrame(frame);
        clearTimeout(timeout);
      };
    }
    setPhase("out");
    const timeout = setTimeout(() => setShown(undefined), MOTION.exit.ms);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const swipeStartY = useRef<number>(undefined);
  function onPointerUp(ev: PointerEvent<HTMLElement>): void {
    const startY = swipeStartY.current;
    swipeStartY.current = undefined;
    if (startY === undefined) return;
    const delta = ev.clientY - startY;
    if (delta > SWIPE_PX) setMinimized(true);
    else if (delta < -SWIPE_PX) setMinimized(false);
  }

  if (!shown) return null;

  const isIn = phase !== "out";
  let transform = "translateY(100%)";
  if (isIn) {
    transform = isMinimized
      ? `translateY(calc(100% - ${ANNOTATION_SHEET_BAR_HEIGHT}px - env(safe-area-inset-bottom)))`
      : "translateY(0)";
  }

  return (
    <Stack
      aria-label="annotation creation sheet"
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 10,
        backgroundColor: "background.paper",
        transform,
        transition: reduceMotion ? "none" : slideTransition(phase),
        boxShadow: isMinimized ? 8 : 0,
      }}
    >
      <Stack
        direction="row"
        onPointerDown={(ev) => (swipeStartY.current = ev.clientY)}
        onPointerUp={onPointerUp}
        sx={{
          height: ANNOTATION_SHEET_BAR_HEIGHT,
          flexShrink: 0,
          px: 2,
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          touchAction: "none",
        }}
      >
        <Stack>
          <FaroText variant="heading14">{title}</FaroText>
          {isMinimized && (
            <FaroText variant="bodyS" sx={{ color: "text.secondary" }}>
              {hasStyle
                ? "Change the style in the toolbar"
                : "Details are kept until you continue"}
            </FaroText>
          )}
        </Stack>
        {/* The style of an annotation lives in the toolbar, so the sheet leads there with a named action */}
        <FaroButton
          variant="ghost"
          icon={
            hasStyle && !isMinimized ? <ColorDot color={color} /> : undefined
          }
          onClick={() => setMinimized(!isMinimized)}
        >
          {sheetAction(isMinimized, hasStyle)}
        </FaroButton>
      </Stack>
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          opacity: isIn ? 1 : 0,
          transition: reduceMotion
            ? "none"
            : `opacity ${CONTENT_FADE_MS}ms ease ${isIn ? MOTION.enter.delayMs : 0}ms`,
          pb: "env(safe-area-inset-bottom)",
        }}
      >
        {shown}
      </Box>
    </Stack>
  );
}

/**
 * @param phase where the sheet is in its life: sliding out, entering, or in and moving between full and minimized
 * @returns the CSS transition of the sheet position for that phase
 */
function slideTransition(phase: "out" | "entering" | "in"): string {
  const motionByPhase = {
    out: MOTION.exit,
    entering: MOTION.enter,
    in: MOTION.toggle,
  };
  const { ms, easing, delayMs = 0 } = motionByPhase[phase];
  return `transform ${ms}ms ${easing} ${delayMs}ms, box-shadow ${ms}ms ${easing}`;
}

/**
 * @param isMinimized true if the sheet is minimized to its bar
 * @param hasStyle true if the annotation has a style, set from the toolbar
 * @returns the label of the button moving the sheet between full and minimized
 */
function sheetAction(isMinimized: boolean, hasStyle: boolean): string {
  if (isMinimized) return "Continue";
  return hasStyle ? "Style" : "Back to view";
}
